import { sub } from 'date-fns';
import _ from 'lodash';
import { Store } from '@/core/flux.service';
import { VantageNames } from '@/vantage/constants';
import type {
  FilterModel,
  RowGroupExpansionState,
  RowGroupState,
  ServerSideRowGroupSelectionState,
  ServerSideRowSelectionState,
} from 'ag-grid-community';
import type { PersistenceLevel } from '@/core/flux.service';
import type { ViewDateRange } from '@/vantage/types/DateTime.types';
import type { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';

/**
 * Default filters for the grid
 * @exposedfortesting
 */
export const defaultFilters = {
  [VantageNames.DerivedColumns.Extinct]: { filterType: 'set', values: ['False'] },
};

export type VantageStoreColumnDef = {
  colId?: string;
  field?: string;
  hide?: boolean | null;
};

export interface GridState {
  filter?: FilterModel;
  column?: VantageStoreColumnDef[];
  row?: {
    group?: RowGroupState;
    groupExpansion?: RowGroupExpansionState;
    selection?: string[] | ServerSideRowSelectionState | ServerSideRowGroupSelectionState;
  };
}

interface VantageTableState {
  selection: SimplifiedNode[];
  gridState: GridState;
  quickFilter: string;
  dateRange: ViewDateRange;
  dateTimeRange: any; // any because of Baobab's `monkey()`;
}

export type VantageTableDehydratedState = Omit<
  VantageTableState,
  'selection' | 'dateTimeRange' | 'gridState' | 'column'
> & {
  gridState: Omit<GridState, 'column'> & {
    // The column state is owned by AG Grid and can have additional properties that we don't want to strictly type, so
    // we allow any properties on the column definition.
    column?: Record<string, unknown>[];
  };
};

export class VantageTableStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  static readonly storeName = 'sqVantageTableStore';

  initialize(): void {
    const initialState: VantageTableState = {
      selection: [],
      gridState: { filter: defaultFilters },
      quickFilter: '',
      dateRange: {
        lookBack: { num: 7, unit: { value: 'day', label: 'day' } },
        lookForward: { num: 0, unit: { value: 'day', label: 'day' } },
        range: [sub(new Date(), { hours: 7 * 24 }), new Date()],
      },
      dateTimeRange: this.monkey(['dateRange'], (dateRange: ViewDateRange) => [
        new Date(dateRange.range?.[0] as Date),
        new Date(dateRange.range?.[1] as Date),
      ]),
    };
    this.state = this.immutable(initialState);
  }

  get selection(): SimplifiedNode[] {
    return this.state.get('selection');
  }

  get gridState(): GridState {
    return this.state.get('gridState');
  }

  get quickFilter(): string {
    return this.state.get('quickFilter');
  }

  get dateRange(): ViewDateRange {
    return this.state.get('dateRange');
  }

  get dateTimeRange(): [Date, Date] {
    return this.state.get('dateTimeRange');
  }

  dehydrate(): VantageTableDehydratedState {
    return _.omit(this.state.serialize(), 'selection', 'dateTimeRange');
  }

  rehydrate(dehydratedState: Partial<VantageTableDehydratedState>): void {
    // Remove the flex property from the column state (it's no longer used and it conflicts with the width setting)
    dehydratedState?.gridState?.column?.forEach((col) => {
      const column = col as { flex?: number | null };
      if (column.flex) {
        delete column.flex;
      }
    });

    this.state.deepMerge(dehydratedState);
  }

  protected readonly handlers = {
    SET_VANTAGE_SELECTION: (payload: { selection: SimplifiedNode[] }) => this.state.set('selection', payload.selection),
    SET_VANTAGE_GRID_STATE: ({ gridState }: { gridState: GridState }) => {
      const filter = { ...defaultFilters, ...gridState.filter };
      this.state.set('gridState', { ...gridState, filter });
    },
    SET_VANTAGE_QUICK_FILTER: (payload: { quickFilter: string }) => this.state.set('quickFilter', payload.quickFilter),
    SET_VANTAGE_DATE_RANGE: (payload: { dateRange: ViewDateRange }) => this.state.set('dateRange', payload.dateRange),
  };
}
